// NodeBarcodeField.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { normalizeBarcodeValue } from 'utilities/Barcode';

// CSS
import './NodeBarcodeField.css';

// Components
import BarcodeScannerModal from 'components/barcodescannermodal/BarcodeScannerModal';
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';

/**
 * NodeBarcodeField
 *
 * Renders a barcode field with manual entry and camera scanning.
 * In readOnly / previewMode (visitor Peek): display-only text, no Scan button.
 *
 * @param {object} node - Node object for field context.
 * @param {string} fieldLabel - Display label for the field.
 * @param {string} value - Current barcode value.
 * @param {function} onChange - Called when the barcode value changes.
 * @param {boolean} [readOnly=false] - Display-only; no edit or scan.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 * @returns {ReactNode} The rendered barcode field.
 */
const NodeBarcodeField = ({
  node,
  fieldLabel,
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);

  // ----- STATE ----- //

  const [barcodeValue, setBarcodeValue] = useState(normalizeBarcodeValue(value));
  const [feedback, setFeedback] = useState('');
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);

  // ----- EFFECTS ----- //

  useEffect(() => {
    setBarcodeValue(normalizeBarcodeValue(value));
  }, [value]);

  // ----- HANDLERS ----- //

  const commitBarcodeValue = useCallback((nextValue) => {
    const normalized = normalizeBarcodeValue(nextValue);
    setBarcodeValue(normalized);
    if (typeof onChange === 'function') {
      onChange(normalized);
    }
  }, [onChange]);

  const handleBarcodeChange = useCallback((event) => {
    if (isReadOnly) return;
    setBarcodeValue(event?.target?.value ?? '');
    setFeedback('');
  }, [isReadOnly]);

  const handleBarcodeBlur = useCallback(() => {
    if (isReadOnly) return;
    commitBarcodeValue(barcodeValue);
  }, [barcodeValue, commitBarcodeValue, isReadOnly]);

  const handleBarcodeDetected = useCallback((nextValue) => {
    commitBarcodeValue(nextValue);
    setFeedback('Barcode captured.');
  }, [commitBarcodeValue]);

  // ----- RENDER ----- //

  return (
    <>
      <div className="node-barcode-field-container">

        <div className="node-barcode-field-input-row">
          <TextInput
            value={barcodeValue}
            placeholder={isReadOnly ? '' : (fieldLabel || 'Barcode')}
            startIconKey="barcode"
            startIconColor={theme.foregroundMuted}
            iconSize={22}
            maxLength={128}
            hybrid
            readOnly={isReadOnly}
            onBlur={handleBarcodeBlur}
            onChange={isReadOnly ? undefined : handleBarcodeChange}
          />

          {!isReadOnly ? (
            <Button
              iconKey="barcode_scanner"
              label="Scan"
              onClick={() => setBarcodeModalOpen(true)}
            />
          ) : null}
        </div>

        {!isReadOnly && feedback ? (
          <div className="node-barcode-field-feedback">
            {feedback}
          </div>
        ) : null}
      </div>

      {!isReadOnly ? (
        <BarcodeScannerModal
          isOpen={barcodeModalOpen}
          label={fieldLabel}
          setOpen={setBarcodeModalOpen}
          onDetected={handleBarcodeDetected}
        />
      ) : null}
    </>
  );
};

NodeBarcodeField.displayName = 'NodeBarcodeField';

export default NodeBarcodeField;
